import React from 'react';
import QuestionEditorPanel from './QuestionEditorPanel';
import PracticeInsightsPanel from './PracticeInsightsPanel';
import { SKILL_TARGET } from '../utils/progress';

function getPracticeStatus(question) {
  const hasAnswer = Boolean((question.studentAnswer || '').trim());
  if (!hasAnswer) return 'unanswered';
  const hasGrading = question.grading !== undefined && question.grading !== null;
  if (!hasGrading) return 'answered';
  return question.grading?.correct ? 'correct' : 'incorrect';
}

function calculateSectionScore(questions) {
  const stats = questions.reduce(
    (stats, question) => {
      const hasGrading = question.grading !== undefined && question.grading !== null;
      if (!hasGrading) return stats;

      if (question.grading?.correct) {
        return {
          ...stats,
          correct: stats.correct + 1,
        };
      }

      return {
        ...stats,
        incorrect: stats.incorrect + 1,
      };
    },
    { correct: 0, incorrect: 0 }
  );

  return {
    ...stats,
    percentage: questions.length > 0 ? Math.round((stats.correct / questions.length) * 100) : 0,
  };
}

// Consecutive correct answers ending at the question being viewed.
function calculateStreak(questions, selectedIndex) {
  let streak = 0;
  for (let i = selectedIndex; i >= 0; i -= 1) {
    if (!questions[i]?.grading?.correct) break;
    streak += 1;
  }
  return streak;
}

function PracticeView({
  practiceQuestions,
  selectedQuestionId,
  onSelectQuestion,
  selectedQuestion,
  onFieldChange,
  onGradeCurrent,
  canGradeCurrent,
  onGrade,
  onNextQuestion,
  onGeneratePractice,
  status,
  practiceSessionId,
  hasGradableAnswers,
  selectedIndex,
  topicName,
  showGeneratePractice,
  authEmail,
  authPassword,
}) {
  const total = practiceQuestions.length;
  const oneBasedIndex = selectedIndex < 0 ? 0 : selectedIndex + 1;
  const isLastQuestion = selectedIndex < 0 || selectedIndex >= total - 1;
  const sectionScore = calculateSectionScore(practiceQuestions);
  const gradedCount = sectionScore.correct + sectionScore.incorrect;
  const feedback = {
    correctCount: sectionScore.correct,
    streak: calculateStreak(practiceQuestions, selectedIndex),
    skillTarget: Math.min(SKILL_TARGET, total),
  };

  return (
    <div className="practice-view">
      <div className="practice-progress-bar">
        <div className="practice-progress-header">
          <span className="practice-progress-accuracy">
            {sectionScore.percentage}% correct
          </span>
          <span className="practice-progress-counter">
            Question {oneBasedIndex} of {total}
          </span>
        </div>
        <div className="practice-progress-segments" role="tablist" aria-label="Switch between questions">
          {practiceQuestions.map((q, idx) => {
            const segStatus = getPracticeStatus(q);
            const isSelected = q.id === selectedQuestionId;
            return (
              <button
                key={q.id}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-label={`Question ${idx + 1}${segStatus !== 'unanswered' ? `, ${segStatus}` : ''}`}
                className={`practice-progress-segment ${isSelected ? 'active' : ''} status-${segStatus}`}
                onClick={() => onSelectQuestion(q.id)}
              >
                {idx + 1}
              </button>
            );
          })}
        </div>
      </div>

      <div className="workspace-body">
        <div className="editor-pane">
          <QuestionEditorPanel
            selectedQuestion={selectedQuestion}
            onFieldChange={onFieldChange}
            onGradeCurrent={onGradeCurrent}
            canGradeCurrent={canGradeCurrent}
            isGrading={status.grading}
            feedback={feedback}
          />
          <div className="practice-view-actions">
            <button
              type="button"
              className="primary full-width"
              onClick={onNextQuestion}
              disabled={status.savingProgress || isLastQuestion || !practiceSessionId}
            >
              {status.savingProgress ? 'Saving…' : isLastQuestion ? 'Last question' : 'Next'}
            </button>
            {showGeneratePractice && (
              <button
                type="button"
                className="ghost generate-practice-btn"
                onClick={onGeneratePractice}
                disabled={status.generatingPractice}
              >
                {status.generatingPractice ? 'Generating…' : 'Generate similar practice problems'}
              </button>
            )}
          </div>
        </div>

        <aside className="questions-pane practice-score-pane">
          <div className="pane-title">Section Accuracy</div>
          <div className="score-card">
            <span className="score-value">{sectionScore.percentage}%</span>
            <span className="score-rules">Correct answers out of {total} questions</span>
          </div>

          <div className="score-breakdown">
            <div className="score-breakdown-item correct">
              <span className="score-breakdown-value">{sectionScore.correct}</span>
              <span className="score-breakdown-label">Correct</span>
            </div>
            <div className="score-breakdown-item incorrect">
              <span className="score-breakdown-value">{sectionScore.incorrect}</span>
              <span className="score-breakdown-label">Wrong</span>
            </div>
            <div className="score-breakdown-item">
              <span className="score-breakdown-value">{total - gradedCount}</span>
              <span className="score-breakdown-label">Ungraded</span>
            </div>
          </div>

          <PracticeInsightsPanel
            authEmail={authEmail}
            authPassword={authPassword}
            refreshKey={gradedCount}
          />
        </aside>
      </div>
    </div>
  );
}

export default PracticeView;
